// supporting functions for comparableGrid.js
import { cloneDeep } from "lodash";
import { comparableDto } from "./ComparableDto";
import { CompGridFormCodes } from "components/Homeowner/CaseForms/ComparableGrid/CompGridFormCodes";
import { map1004pPostAdjustment, map1004pPostDesc } from "components/Homeowner/CaseForms/ComparableGrid/1004pPost";
import { mapFannieMaePostDesc, mapFannieMaePostAdjustment } from "components/Homeowner/CaseForms/ComparableGrid/FannieMaePost";
import { mapMValuePostDesc, mapMValuePostAdjustment } from "components/Homeowner/CaseForms/ComparableGrid/MValuePost";
import { mapBBTMValuePostAdjustment, mapBBTMValuePostDesc } from "components/Homeowner/CaseForms/ComparableGrid/BBTMValuePost";
import { Form1004pSections, Form1004pComparableCount } from "components/Homeowner/CaseForms/ComparableGrid/Sections/Form1004pSections";
import { FannieMaeHybridSections, FannieMaeHybridComparableCount } from "components/Homeowner/CaseForms/ComparableGrid/Sections/FannieMaeHybridSections";
import { MValueSections, MValueComparableCount } from "components/Homeowner/CaseForms/ComparableGrid/Sections/MValueSections";
import { BBTMValueSections, BBTMValueComparableCount } from "components/Homeowner/CaseForms/ComparableGrid/Sections/BBTMValueSections";
import { ReoMitSections, ReoMitComparableCount } from "components/Homeowner/CaseForms/ComparableGrid/Sections/ReoMitSections";
import * as sections from "components/Homeowner/CaseForms/ComparableGrid/Sections/Sections";
import { mapReoMitPostAdjustment, mapReoMitPostDesc } from "components/Homeowner/CaseForms/ComparableGrid/REOMITpost";
import { ChaseServicingSections, ChaseServicingComparableCount } from "components/Homeowner/CaseForms/ComparableGrid/Sections/ChaseServicingSections";
import { mapChaseServicingPostAdjustment, mapChaseServicingPostDesc } from "components/Homeowner/CaseForms/ComparableGrid/ChaseServicingPost";

export const copySubjectBlacklist = [sections.VALUE_ADJUSTMENTS, sections.SALE_DATE_BBT, sections.COMPARISON_TO_SUBJECT, sections.SALE_DATE_FNMA, sections.CDOM, sections.DAYS_ON_MARKET, sections.DATA_SOURCES_FNMA,
sections.SALE_PRICE, sections.FINANCING_CONCESSIONS, sections.DATE_OF_SALE_TIME, sections.OTHER_NAME_1, sections.OTHER_NAME_2, sections.OTHER_NAME_3,
sections.DATA_SOURCES_1004P, sections.NET_ADJ, sections.ADJ_NET_PRICE, sections.ADJ_GROSS_PRICE, sections.ADDRESS, sections.ADDRESS_2, sections.PROXIMITY_TO_SUBJECT,
sections.SALE_PRICE_GLA, sections.VERIFICATION_SOURCES, sections.ABOVE_GRADE, sections.ABOVE_GRADE_BBT, sections.GLA, sections.SALE_OR, sections.COMPARABLE_TYPE, sections.BELOW_GRADE];

export const adjustments = {
  "above-grade": ["Total Factor", "Bedroom Factor", "Bathroom Factor", "Half Bathroom Factor"],
  "unit-1": ["Total Factor", "Bedroom Factor", "Bathroom Factor", "Half Bathroom Factor"],
  "unit-2": ["Total Factor", "Bedroom Factor", "Bathroom Factor", "Half Bathroom Factor"],
  "unit-3": ["Total Factor", "Bedroom Factor", "Bathroom Factor", "Half Bathroom Factor"],
  "unit-4": ["Total Factor", "Bedroom Factor", "Bathroom Factor", "Half Bathroom Factor"],
  "basement-finished": ["Total SF", "Finished SF", "Exit Type"],
  "concession": ["Adj Factor"],
  "site": ["Adj Threshold", "Adj Factor"],
  "view": ["View rating adj factor", "View type adj factor"],
  "age": ["Age Adj Factor"],
  "gla": ["Adj Threshold", "Adj Factor"]
};

// Get dropdown list from data recieved from API and add an empty value in the beginning.
export const getOptionsFromDisplayValue = data => {
  let options = data.map(x => ({ label: x.displayValue, value: x.storedValue }));
  return [{ label: "", value: "" }].concat(options);
};

// converts bool value to yes or no
export const boolToDisplayValue = bool => (bool !== false ? (bool ? "yes" : null) : "no");

// catch all for ways to display true/false as a string - different from the above method in that it is a capital Yes/No   
export const boolToDisplayValueHack = bool => ((bool !== false && bool !== "false" && bool !== "False" && bool !== "No") ? (bool === true || bool === "True" || bool === "true" || bool === "Yes" ? "Yes" : null) : "No");

// converts yes or no to bool
export const displayValueToBool = value => (value ? (value === "yes" ? true : false) : null);

export const noValueDash = (val) => (!val && val !== 0 && "-") || val;

export const hasValue = val => !!val || val === 0;

export const isValidNumFieldValue = (n, allowNeg = false, allowDecimal = false) => {
  if (n === undefined || n === null) return false;
  else if (typeof (n) === "string" && n.includes(".") && !allowDecimal) return false;
  else if (n === "") return true;
  else if (+n === 0) return true;
  else if (n === "-" && allowNeg) return true;
  else if (+n) {
    const decimal = +n % 1 !== 0 ? allowDecimal : true;
    const neg = +n < 0 ? allowNeg : true;
    return decimal && neg;
  }
  return false;
};

export const isValidValue = (newValue, subSectionDir) => {
  if (subSectionDir.numOnly) {
    let allowNeg = subSectionDir.allowNeg;
    let allowDecimal = subSectionDir.allowDecimal;
    return isValidNumFieldValue(newValue, allowNeg, allowDecimal);
  }
  return true;
};

export const aboveGradeUnitRoomCounts = (table, index, direction, subTableKeyMappings) => {
  let totalCount = table[index][subTableKeyMappings.total][direction].value;
  let bedrooms = table[index][subTableKeyMappings.bedrooms][direction].value;
  let bathrooms = table[index][subTableKeyMappings.bathrooms][direction].value;
  let halfBathrooms = table[index][subTableKeyMappings.halfBathrooms][direction].value;
  return [noValueDash(totalCount), noValueDash(bedrooms), `${noValueDash(bathrooms)}.${noValueDash(halfBathrooms)}`];
};

export const belowGradeRoomCountOptions = (table, index, direction) => {
  let recCount = table[index][sections.REC][direction].value;
  let bedrooms = table[index][sections.BED][direction].value;
  let bathrooms = table[index][sections.FULL_BATH][direction].value;
  let halfBathrooms = table[index][sections.HALF_BATH][direction].value;
  let other = table[index][sections.OTHER][direction].value;
  return [noValueDash(recCount), noValueDash(bedrooms), `${noValueDash(bathrooms)}.${noValueDash(halfBathrooms)}`, noValueDash(other)];
};


export const getAboveGradeUnitSubTableKeysFromGroupValue = (groupValue) => {
  let valueToSubTableKeyMappings = {
    "above-grade": {
      head: sections.ABOVE_GRADE_ROOM_COUNT, total: sections.TOTAL, bedrooms: sections.BEDROOMS, bathrooms: sections.BATHROOMS, halfBathrooms: sections.HALF_BATHROOMS
    },
    "above-grade-bbt": {
      head: sections.ABOVE_GRADE_ROOM_COUNT_BBT, total: sections.TOTAL_BBT, bedrooms: sections.BEDROOMS_BBT, bathrooms: sections.BATHROOMS_BBT, halfBathrooms: sections.HALF_BATHROOMS_BBT
    }
  };

  return valueToSubTableKeyMappings[groupValue];
};

export const garageFieldsByFormCode = (comp) => {
  if (Object.keys(comp).includes(sections.CAR_STORAGE_2_BBT)) {
    return {
      garageCarport: sections.GARAGE_CARPORT_BBT,
      driveway: sections.DRIVEWAY_BBT,
      numOfCars: sections.NUM_OF_CARS_BBT,
      carStorage1: sections.CAR_STORAGE_1_BBT,
      attachmentType1: sections.ATTACHMENT_TYPE_1_BBT,
      numOfCars1: sections.NUM_OF_CARS_1_BBT,
      carStorage2: sections.CAR_STORAGE_2_BBT,
      attachmentType2: sections.ATTACHMENT_TYPE_2_BBT,
      numOfCars2: sections.NUM_OF_CARS_2_BBT,
    };
  }

  if (Object.keys(comp).includes(sections.CAR_STORAGE_2_MVALUE)) {
    return {
      garageCarport: sections.GARAGE_CARPORT_MVALUE,
      driveway: sections.DRIVEWAY_ENABLED,
      numOfCars: sections.NUM_OF_CARS_MVALUE,
      carStorage1: sections.CAR_STORAGE_1_MVALUE,
      attachmentType1: sections.ATTACHMENT_TYPE_1_MVALUE,
      numOfCars1: sections.NUM_OF_CARS_1_MVALUE,
      carStorage2: sections.CAR_STORAGE_2_MVALUE,
      attachmentType2: sections.ATTACHMENT_TYPE_2_MVALUE,
      numOfCars2: sections.NUM_OF_CARS_2_MVALUE,
    };
  }
  
  return {
    garageCarport: sections.GARAGE_CARPORT,
    driveway: sections.DRIVEWAY,
    numOfCars: sections.NUM_OF_CARS,
    carStorage1: sections.CAR_STORAGE_1,
    attachmentType1: sections.ATTACHMENT_TYPE_1,
    numOfCars1: sections.NUM_OF_CARS_1,
    carStorage2: sections.CAR_STORAGE_2,
    attachmentType2: sections.ATTACHMENT_TYPE_2,
    numOfCars2: sections.NUM_OF_CARS_2,
  };
};

export const getGarageSectionsByFormCode = (comp) => {
  if(Object.keys(comp).includes(sections.CAR_STORAGE_2_BBT)){
    return [sections.CAR_STORAGE_2_BBT, sections.ATTACHMENT_TYPE_2_BBT, sections.NUM_OF_CARS_2_BBT];
  }
  if(Object.keys(comp).includes(sections.CAR_STORAGE_2_MVALUE)){
    return [sections.CAR_STORAGE_2_MVALUE, sections.ATTACHMENT_TYPE_2_MVALUE, sections.NUM_OF_CARS_2_MVALUE];
  }
  return [sections.CAR_STORAGE_2, sections.ATTACHMENT_TYPE_2, sections.NUM_OF_CARS_2];
};

// dont hide extra car storage on rerender if any of the subject or comp fields has value in it
export const hideExtraCarStorage = (comp, dir) => {
  let extraCarStorageSections = [];
  extraCarStorageSections = getGarageSectionsByFormCode(comp);
  return !extraCarStorageSections.some(x => comp[x][dir].value);
};

export const reorder = (list, startIndex, endIndex) => {
  const result = cloneDeep(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const getFormSections = (formCode) => {
  switch (formCode) {
    case CompGridFormCodes.URAR_1004_APPRAISALCOMPARABLES:
      return Form1004pSections;
    case CompGridFormCodes.FNMA_APPRAISAL_COMPARABLE:
      return FannieMaeHybridSections;
    case CompGridFormCodes.REOMIT_APPRAISAL_COMPARABLES:
      return ReoMitSections;
    case CompGridFormCodes.COLLATERAL_APPRAISAL_COMPARABLES:
      return MValueSections;
    case CompGridFormCodes.BBT_COMPARABLES_GRID_MUELLER_ENTERPRISE:
      return BBTMValueSections;
    case CompGridFormCodes.CHASE_SERVICING_COMPARABLES:
      return ChaseServicingSections;
  }
};

export const hasRepeatedAddresses = (table) => {
  let emptyAddressCount = 0;
  const generateAddressString = (comp, dir) => {
    const orEmpty = val => val || "";
    let addressString = "" + orEmpty(comp[sections.ADDRESS][dir].value) + orEmpty(comp[sections.ADDRESS_2][dir].value) + orEmpty(comp[sections.CITY][dir].value) + orEmpty(comp[sections.STATE][dir].value);
    if (addressString.length > 0) return addressString.toLowerCase();
    return null;
  };
  let set = new Set();
  table.forEach((comp, index) => {
    if (index === 0) {
      let addressString = generateAddressString(comp, "right");
      if (addressString) set.add(addressString);
      else emptyAddressCount++;
    } else {
      let addressString = generateAddressString(comp, "left");
      if (addressString) set.add(addressString);
      else emptyAddressCount++;
    }
  });
  if ((set.size + emptyAddressCount) !== table.length) {
    return true;
  }
  return false;
};

export const comparableCountByFormCode = {
  [CompGridFormCodes.URAR_1004_APPRAISALCOMPARABLES]: Form1004pComparableCount,
  [CompGridFormCodes.FNMA_APPRAISAL_COMPARABLE]: FannieMaeHybridComparableCount,
  [CompGridFormCodes.REOMIT_APPRAISAL_COMPARABLES]: ReoMitComparableCount,
  [CompGridFormCodes.COLLATERAL_APPRAISAL_COMPARABLES]: MValueComparableCount,
  [CompGridFormCodes.BBT_COMPARABLES_GRID_MUELLER_ENTERPRISE]: BBTMValueComparableCount,
  [CompGridFormCodes.CHASE_SERVICING_COMPARABLES]: ChaseServicingComparableCount,
};

export const convertTypeToReadOnly = sec => {
  // Converting types to "display"
  // These types should not be converted to "display"
  // "None" when converted to "display" will show the hidden value
  // "Room-count" is a special type to display Total, Bed, Bath in one cell
  // "Date" is disabled by passing prop. Converting it to display will display time string
  let typesToNotConvert = ["none", "room-count", "date"];
  for (let dir of ["left", "right"]) {
    if (sec && sec[dir] && sec[dir].type && !typesToNotConvert.includes(sec[dir].type)) {
      sec[dir].type = "display";
    }
  }
  return sec;
};

export const postCompData = (table, compGridFormCode) => {
  const postFunc = {
    [CompGridFormCodes.URAR_1004_APPRAISALCOMPARABLES]: {
      adjustment: map1004pPostAdjustment,
      desc: map1004pPostDesc
    },
    [CompGridFormCodes.FNMA_APPRAISAL_COMPARABLE]: {
      adjustment: mapFannieMaePostAdjustment,
      desc: mapFannieMaePostDesc
    },
    [CompGridFormCodes.REOMIT_APPRAISAL_COMPARABLES]: {
      adjustment: mapReoMitPostAdjustment,
      desc: mapReoMitPostDesc
    },
    [CompGridFormCodes.COLLATERAL_APPRAISAL_COMPARABLES]: {
      adjustment: mapMValuePostAdjustment,
      desc: mapMValuePostDesc
    },
    [CompGridFormCodes.BBT_COMPARABLES_GRID_MUELLER_ENTERPRISE]: {
      adjustment: mapBBTMValuePostAdjustment,
      desc: mapBBTMValuePostDesc
    },
    [CompGridFormCodes.CHASE_SERVICING_COMPARABLES]: {
      adjustment: mapChaseServicingPostAdjustment,
      desc: mapChaseServicingPostDesc
    }
  };

  let subject = cloneDeep(comparableDto);
  let comparables = [];
  table.map((item, index) => {
    if (index === 0) {
      postFunc[compGridFormCode].desc(subject, item, "right");
    } else {
      let comparable = cloneDeep(comparableDto);
      postFunc[compGridFormCode].adjustment(comparable, item, "right");
      postFunc[compGridFormCode].desc(comparable, item, "left");
      comparables.push(comparable);
    }
  });
  return { subject, comparables };
};
